import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, firstValueFrom } from 'rxjs';
import * as UserProfileActions from 'src/app/core/store/nowaccount/actions/user-profile.actions';
import * as CreateAccountActions from 'src/app/core/store/onboarding/actions/create-account.actions';
import { environment } from 'src/environments/environment';
import { AbstractLoginService } from '../../abstracts/services/login/login.abstract';
import { IApiResponse } from '../../interfaces/api/api-response.interface';
import { IForgotPassword } from '../../interfaces/login/forgotpassword.interface';
import { ILoginResponseMessage } from '../../interfaces/login/login-response-message.interface';
import { ILogin } from '../../interfaces/login/login.interface';
import { IResetPassword } from '../../interfaces/login/resetpassword.interface';
import { IPhoneNumber } from '../../interfaces/login/setphonenumber.interface';
import { IVerificationUser } from '../../interfaces/login/verification.interface';
import { IVerifyPhoneNumber } from '../../interfaces/login/verifyphonenumber.interface';
import { ClearAction } from '../../store/common/actions/clear.action';
import { IAppState } from '../../store/common/interfaces/app-state.interface';

@Injectable({ providedIn: 'root' })
export class LoginService extends AbstractLoginService {
  private apiUrl = environment.apiUrl;
  private controllerSuffix = 'OnBoarding/Login/';
  constructor(
    private http: HttpClient,
    private store: Store<IAppState>,
  ) {
    super();
  }

  // login
  async userlogin(login: ILogin): Promise<ILoginResponseMessage> {
    const loginUrl = `${this.apiUrl}${this.controllerSuffix}Login`;
    const res = await firstValueFrom(
      this.http.post<ILoginResponseMessage>(loginUrl, login),
    );
    this.populateUserLogin(res);
    return res;
  }

  autoLogin(payload: ILogin): Observable<any> {
    const url = `${this.apiUrl}${this.controllerSuffix}AutoLogin`;
    return this.http.post(url, payload);
  }

  verification(verification: IVerificationUser): Observable<any> {
    const loginUrl = `${this.apiUrl}${this.controllerSuffix}Verification`;
    return this.http.post(loginUrl, verification);
  }

  getUserEmailAndPhone(): Observable<any> {
    const url = `${this.apiUrl}${this.controllerSuffix}GetUserEmailAndPhone`;
    return this.http.get(url);
  }

  async sendEmailCode(email: string): Promise<any> {
    const url = `${this.apiUrl}${this.controllerSuffix}SendEmailCode`;
    const body = { email };
    const res = await firstValueFrom(this.http.post(url, body));
    return res;
  }

  async verifyEmailCode(code: string): Promise<ILoginResponseMessage> {
    const url = `${this.apiUrl}${this.controllerSuffix}VerifyEmailCode`;
    const body = { code };
    const res = await firstValueFrom(
      this.http.post<ILoginResponseMessage>(url, body),
    );
    this.populateUserLogin(res);
    return res;
  }

  async reSendEmailCode(): Promise<any> {
    const url = `${this.apiUrl}${this.controllerSuffix}ReSendEmailCode`;
    const res = await firstValueFrom(this.http.get(url));
    return res;
  }

  async sendPhoneCode(email: string): Promise<any> {
    const url = `${this.apiUrl}${this.controllerSuffix}SendPhoneCode`;
    const body = { email };
    const res = await firstValueFrom(this.http.post(url, body));
    return res;
  }

  async verifyPhoneCode(code: string): Promise<ILoginResponseMessage> {
    const url = `${this.apiUrl}${this.controllerSuffix}VerifyPhoneCode`;
    const body = { code };
    const res = await firstValueFrom(
      this.http.post<ILoginResponseMessage>(url, body),
    );
    this.populateUserLogin(res);
    return res;
  }

  async reSendPhoneCode(): Promise<any> {
    const url = `${this.apiUrl}${this.controllerSuffix}ReSendPhoneCode`;
    const res = await firstValueFrom(this.http.get(url));
    return res;
  }

  async forgotPassword(forgotpassword: IForgotPassword): Promise<any> {
    const url = `${this.apiUrl}${this.controllerSuffix}ForgotPassword`;
    const res = await firstValueFrom(this.http.post(url, forgotpassword));
    return res;
  }

  resetPassword(resetPassword: IResetPassword): Observable<any> {
    const url = `${this.apiUrl}${this.controllerSuffix}ResetPassword`;
    return this.http.post(url, resetPassword);
  }

  signInWithIntuit(): Observable<any> {
    const url = `${this.apiUrl}${this.controllerSuffix}SignInWithIntuit`;
    return this.http.get(url);
  }

  logout(): Observable<IApiResponse> {
    this.store.dispatch(ClearAction());
    const body = { isLogout: true };
    const url = `${this.apiUrl}${this.controllerSuffix}LogOff`;
    return this.http.post<IApiResponse>(url, body);
  }

  rememberMe(rememberMe: Boolean): Observable<any> {
    const body = { rememberMe: rememberMe };
    const url = `${this.apiUrl}${this.controllerSuffix}RememberMe`;
    return this.http.post<IApiResponse>(url, body);
  }

  // Update user's phone number
  sendUserPhoneCode(phoneNumber: IPhoneNumber): Observable<any> {
    const url = `${this.apiUrl}${this.controllerSuffix}SetPhoneNumber`;
    return this.http.post<IApiResponse>(url, phoneNumber);
  }

  verifyUserPhoneNumber(confirmPhone: IVerifyPhoneNumber): Observable<any> {
    const url = `${this.apiUrl}${this.controllerSuffix}VerifyPhoneNumber`;
    return this.http.post<IApiResponse>(url, confirmPhone);
  }

  private populateUserLogin(obj: any) {
    console.log('Begin populateUserLogin');
    console.log(obj);
    //if (this.isLoginResponseMessage(obj)) {
    console.log('populateUserLogin isLoginResponseMessage');
    const userProfileId = obj.UserProfileId;
    const userEmail = obj.UserEmail;
    const userFirstName = obj.UserFirstName;
    const userLastName = obj.UserLastName;

    this.store.dispatch(
      UserProfileActions.UserProfileIdAction({
        userProfileId,
      }),
    );

    this.store.dispatch(
      CreateAccountActions.LoginAction({
        email: userEmail,
        firstName: userFirstName,
        lastName: userLastName,
      }),
    );
    //}
    console.log('End populateUserLogin');
  }

  private isLoginResponseMessage(obj: any): obj is ILoginResponseMessage {
    return (
      typeof obj.Success === 'boolean' &&
      typeof obj.Code === 'string' &&
      typeof obj.Message === 'string' &&
      typeof obj.SignInStatus === 'number' &&
      typeof obj.IsPhoneNumberRequired === 'boolean' &&
      typeof obj.IsLegacyUpgrade === 'boolean' &&
      typeof obj.LoggedInRole === 'number' && // Assuming LoggedInRole is a string, change if it's a different type
      typeof obj.UserProfileId === 'number' &&
      typeof obj.UserEmail === 'string' &&
      typeof obj.UserFirstName === 'string' &&
      typeof obj.UserLastName === 'string'
    );
  }
}
